import React, { useRef } from 'react';
import { Typography } from '@mui/material';
import styled from '@emotion/styled';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { EllipsisTooltip } from '@/exports/columnCommonComponents';
import { OVERLAY_KIND, SEGMENT_KIND } from '@/redux/slices/packagingSlice.types';
import { getOptionColor } from '@/components/Packaging/utils';

interface Props {
  kind: SEGMENT_KIND | OVERLAY_KIND;
  label: string;
  toBeRemoved?: boolean;
  draggable?: boolean;
  assignedTitleName?: string;
}

// TODO: split this component into two separate components. One to be used in the `DraggableEditorOptions` and the other
//  one in the Templates Swap modal. They are two pretty different use cases and it doesn't look good mixing them.
//  Possibly move a common visual part to a separate component too.
const EditorOption: React.FC<Props> = ({ kind, label, toBeRemoved = false, draggable = false, assignedTitleName }) => {
  const chipDataRef = useRef<HTMLDivElement>(null);
  const color = getOptionColor(kind);

  return (
    <CutTypeChip removed={+toBeRemoved}>
      <ColoredTab color={color} removed={+toBeRemoved} />
      <div ref={chipDataRef} style={{ width: '100%', overflow: 'hidden' }}>
        <ChipData removed={+toBeRemoved}>
          {!toBeRemoved && draggable && <StyledGripIcon icon="grip" height={20} width={20} color={color} />}
          <ChipLabel removed={+toBeRemoved} drag={+!draggable}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', columnGap: 6 }}>
                {!toBeRemoved && <PolarisIcon icon="cards" height={20} width={20} />}
                <Typography variant="h6">
                  <EllipsisTooltip maxWidth={(chipDataRef?.current ? chipDataRef?.current?.clientWidth - 52 : 250) * 0.7} text={label} />
                </Typography>
              </div>
              {toBeRemoved && <RemoveWarningSpan>{label} will be removed if used.</RemoveWarningSpan>}
              {assignedTitleName && <AssignedTitle>{assignedTitleName}</AssignedTitle>}
            </div>
          </ChipLabel>
          {toBeRemoved && <RemoveIcon icon="close_outline" height={20} width={20} color="error" />}
        </ChipData>
      </div>
    </CutTypeChip>
  );
};

export default EditorOption;

const CutTypeChip = styled.div<{ removed: number }>`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  margin: ${({ theme }) => theme.spacing(1)};
  min-height: 58px;
  overflow: hidden;

  background: ${({ theme, removed }) => {
    if (removed) return theme.palette.background.paper;
    return theme.palette.mode === 'dark' ? '#303242' : '#f4f4f4';
  }};
  border: ${({ theme, removed }) => (removed ? `1px solid ${theme.palette.error.main + '5a'}` : 'none')};
`;

const ColoredTab = styled.span<{ color: string; removed: number }>`
  background: ${({ color, theme, removed }) => (removed ? theme.palette.error.main : color)};
  width: 5px;
  align-self: stretch;
`;

const StyledGripIcon = styled(PolarisIcon)<{ color: string }>`
  color: ${({ color }) => color};
  min-width: 20px;
  flex-grow: 0;
`;

const ChipData = styled.div<{ removed: number }>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  padding: ${({ theme }) => theme.spacing(1, 2, 1, 1)};
  width: 100%;
`;

const ChipLabel = styled.div<{ drag: number; removed: number }>`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  column-gap: ${({ theme }) => theme.spacing(1)};
  padding-left: ${({ theme, removed, drag }) => theme.spacing(removed || drag ? 1 : 2)};

  > div {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    text-align: left;
    column-gap: ${({ theme }) => theme.spacing(1)};
    width: 100%;
  }

  .MuiTypography-h6 {
    font-size: 1.2rem;
  }
`;

const RemoveWarningSpan = styled.span`
  color: ${({ theme }) => theme.palette.error.main};
  opacity: 0.7;
  font-size: 0.8rem;
`;

const RemoveIcon = styled(PolarisIcon)`
  color: ${({ theme }) => theme.palette.error.main};
  opacity: 0.7;
`;

const AssignedTitle = styled.span`
  font-size: 0.8rem;
`;
